import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { City, useCities, useLocationStore } from '@entities/Location'

import { cn } from '@shared/lib/cn'

import CitySuggestion from './CitySuggestion/CitySuggestion'

import styles from './styles.module.scss'

interface LocationPickerProps {
  className?: string;
  currentCity: City | null;
  onCitySelect?: (city: City) => void;
}

export default function LocationPicker ({ className, currentCity, onCitySelect }: LocationPickerProps) {
  const { t } = useTranslation('common')
  const [ isOpen, setIsOpen ] = useState(false)
  const [ searchQuery, setSearchQuery ] = useState('')
  const [ needToConfirmSelectedCity, setNeedToConfirmSelectedCity ] = useState(false)

  const { cities, loading: citiesLoading } = useCities()
  const { setCurrentCity } = useLocationStore()

  // Validate stored city from localStorage
  // Check if the stored city exists in the available cities list
  useEffect(() => {
    // Wait for cities to load
    if (citiesLoading || !cities.length) return

    // If no city is stored, nothing to validate
    if (!currentCity) {
      console.log('LocationPicker: No city to validate, set first from list')

      // setCurrentCity(cities[0])
      onCitySelect?.(cities[0])
      setNeedToConfirmSelectedCity(true)
      return
    }

    console.log('LocationPicker: Validating stored city:', {
      storedCity: currentCity,
      availableCities: cities.map(c => ({ id: c.id, name: c.name, shopId: c.defaultShop?.id })),
    })

    // Validate that the stored city exists in the available cities list
    const isValidCity = cities.some((city) => {
      // Check if city ID matches
      if (city.id !== currentCity.id) return false

      // Check if city name matches (prevent XSS with modified names)
      if (city.name !== currentCity.name) {
        console.warn('City name mismatch:', {
          stored: currentCity.name,
          available: city.name,
        })
        return false
      }

      // Check if shop ID matches (if defaultShop exists in stored city)
      // Note: We only validate if the stored city has a shop ID
      // If the stored city doesn't have a shop ID, we still consider it valid
      // (the shop might have been added/removed from the API)
      if (currentCity.defaultShop?.id) {
        // If stored city has shop ID, it should match
        if (city.defaultShop?.id !== currentCity.defaultShop.id) {
          console.warn('City shop ID mismatch:', {
            storedShopId: currentCity.defaultShop.id,
            availableShopId: city.defaultShop?.id,
          })
          return false
        }
      }

      return true
    })

    // If validation fails, reset the city
    if (!isValidCity) {
      console.warn('Stored city validation failed, resetting city:', {
        storedCity: currentCity,
        availableCities: cities.map(c => ({ id: c.id, name: c.name })),
      })
      setCurrentCity(null)
    }
    else {
      console.log('LocationPicker: City validation passed')
    }
  }, [ cities, citiesLoading, currentCity, onCitySelect, setCurrentCity ])

  const filteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleCitySelect = (city: City) => {
    onCitySelect?.(city)
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleDismissSuggestion = () => {
    setIsOpen(true)
  }

  const handleToggle = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setSearchQuery('')
    }
  }

  return (
    <div className={cn(styles.locationPicker, className)}>
      <button
        className={styles.locationPicker__trigger}
        onClick={handleToggle}
        type="button"
      >
        <img
          src="/icons/location-picker.svg"
          alt="Location"
          className={styles.locationPicker__icon}
        />
        <span className={styles.locationPicker__text}>
          {currentCity?.name ?? t('location.chooseCity')}
        </span>
        <img
          src="/icons/arrow.svg"
          alt=""
          className={cn(styles.locationPicker__arrow, {
            [styles.locationPicker__arrowOpen]: isOpen,
          })}
        />
      </button>

      {isOpen && (
        <div className={styles.locationPicker__popup}>
          <div className={styles.locationPicker__header}>
            <h3 className={styles.locationPicker__title}>
              {t('location.yourCity')}
            </h3>
          </div>

          <div className={styles.locationPicker__search}>
            <div className={styles.locationPicker__searchInput}>
              <img
                src="/icons/search.svg"
                alt=""
                className={styles.locationPicker__searchIcon}
              />
              <input
                type="text"
                placeholder={t('location.enterCityName')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.locationPicker__input}
              />
            </div>
          </div>

          <div className={styles.locationPicker__list}>
            {citiesLoading ? (
              <div className={styles.locationPicker__loading}>
                {t('common.loading')}...
              </div>
            ) : (
              <ul className={cn(styles.locationPicker__cityList, 'reset-list')}>
                {filteredCities.map((city) => (
                  <li key={city.id} className={styles.locationPicker__cityItem}>
                    <button
                      className={cn(styles.locationPicker__cityButton, {
                        [styles.locationPicker__cityButtonActive]: city.id === currentCity?.id,
                      })}
                      onClick={() => handleCitySelect(city)}
                      type="button"
                    >
                      {city.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className={styles.locationPicker__overlay}
          onClick={() => setIsOpen(false)}
        />
      )}

      <CitySuggestion
        currentCity={currentCity}
        onCitySelect={handleCitySelect}
        onDismiss={handleDismissSuggestion}
        isLocationPickerOpen={isOpen}
        isConfirmation={needToConfirmSelectedCity}
      />
    </div>
  )
}
